import { Card, CardContent, RadioGroup, Stack } from '@mui/joy';
import { useTranslation } from 'react-i18next';

import { Confirmation } from 'core/components';
import { SelectSpaButton } from 'quotes/components/QuotePartSelectSpa';
import { ExternalPricingSourceCard, PricingDateFormField } from 'quotes/elements';
import { PriceBasedOn } from 'quotes/enums';
import { getEffectiveSpaInfo } from 'quotes/utils';
import { Color, Direction, Variant } from 'theme/enums';

import { PurchaseCostFormFields } from './PurchaseCostFormFields';
import { ReplacementCostFormFields } from './ReplacementCostFormFields';
import { ShipAndDebitVendorWarning } from './ShipAndDebitVendorWarning';
import { useCostFormFieldsContext } from './state';

export const CostFormFields = () => {
  const { t } = useTranslation();

  const {
    costSourceDisplayData,
    isAlternateVersionEnabled,
    isShipAndDebitVendor,
    displayReplacementCost,
    liveExternalReplacementCost,
    isCostSourceExternal,
    disablePricingFields,
    priceBasedOn,
    selectedSpa,
    defaultSpa,
    isReplaceSelectedSpaConfirmationOpen,
    replaceSelectedSpaAgreementCode,
    confirmReplaceSelectedSpa,
    cancelReplaceSelectedSpa,
    onPriceBasedOnChange,
    onChangePricingDate,
    canSelectSpa,
    canRemoveSelectedSpa,
    buildSelectSpaRequest,
    markSelectSpaPress,
    pricingWindowMessage,
    selectedSpaNotOfferedMessage,
    onHeldAgreementOffered,
    quotePart,
    getValues,
    selectSpa,
    clearSelectedSpa,
  } = useCostFormFieldsContext();

  const pricingDateField = isAlternateVersionEnabled && (
    <PricingDateFormField onChange={() => onChangePricingDate()} />
  );

  const selectSpaAction =
    canSelectSpa || canRemoveSelectedSpa ? (
      <SelectSpaButton
        buildRequestPayload={buildSelectSpaRequest}
        onUse={selectSpa}
        onRemove={clearSelectedSpa}
        onHeldAgreementOffered={onHeldAgreementOffered}
        onPress={markSelectSpaPress}
        partNumber={getValues('part')?.number ?? quotePart?.partArr?.number}
        selectedSpa={selectedSpa}
        defaultSpa={defaultSpa}
        canSelect={canSelectSpa}
        unavailableMessage={pricingWindowMessage}
        disabled={disablePricingFields}
      />
    ) : null;

  // Mounted here: the purchase-cost fields render in two branches and re-render on every recalc.
  const replaceSelectedSpaConfirmation = (
    <Confirmation
      isOpen={isReplaceSelectedSpaConfirmationOpen}
      title={t('titleReplaceSelectedSpa')}
      message={t('messageConfirmReplaceSelectedSpa', {
        agreementCode: replaceSelectedSpaAgreementCode,
      })}
      onConfirm={confirmReplaceSelectedSpa}
      onCancel={cancelReplaceSelectedSpa}
    />
  );

  // costSourceDisplayData, not costData: with the overwrite-cost toggle on, costData still reports
  // the pre-override source until a recalculation lands, which would present the SPA as applied.
  const effectiveSpa = getEffectiveSpaInfo(costSourceDisplayData);

  const externalPricingSourceCard = (
    <ExternalPricingSourceCard
      pricingData={effectiveSpa.pricingData}
      spaCostCurrency={effectiveSpa.spaCostCurrency}
      selectedSpaNotOfferedMessage={selectedSpaNotOfferedMessage}
      action={selectSpaAction}
      isActionOnlyVisible={!!selectSpaAction}
      isAlternateVersionEnabled={isAlternateVersionEnabled}
      isShipAndDebitVendor={isShipAndDebitVendor}
    />
  );

  if (!displayReplacementCost) {
    return (
      <>
        {pricingDateField}

        {externalPricingSourceCard}

        <Card variant={Variant.SOFT} color={Color.PRIMARY}>
          <CardContent>
            <PurchaseCostFormFields inline />
          </CardContent>
        </Card>

        {replaceSelectedSpaConfirmation}
      </>
    );
  }

  return (
    <>
      {pricingDateField}

      {externalPricingSourceCard}

      <ShipAndDebitVendorWarning
        isCostSourceExternal={isCostSourceExternal}
        externalReplacementCost={liveExternalReplacementCost}
      />

      <RadioGroup value={priceBasedOn} onChange={onPriceBasedOnChange}>
        <Stack direction={Direction.ROW} gap={2}>
          {/* Left card: Replacement Cost */}
          <Card
            variant={priceBasedOn === PriceBasedOn.REPLACEMENT ? Variant.SOFT : Variant.PLAIN}
            color={priceBasedOn === PriceBasedOn.REPLACEMENT ? Color.PRIMARY : Color.NEUTRAL}
            sx={{ flex: 1 }}
          >
            <CardContent>
              <ReplacementCostFormFields />
            </CardContent>
          </Card>

          {/* Right card: Quote Cost */}
          <Card
            variant={priceBasedOn === PriceBasedOn.QUOTED ? Variant.SOFT : Variant.PLAIN}
            color={priceBasedOn === PriceBasedOn.QUOTED ? Color.PRIMARY : Color.NEUTRAL}
            sx={{ flex: 1 }}
          >
            <CardContent>
              <PurchaseCostFormFields />
            </CardContent>
          </Card>
        </Stack>
      </RadioGroup>

      {replaceSelectedSpaConfirmation}
    </>
  );
};
